import React, { useCallback, useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision';
import { analyzeFingers } from '../lib/handPose.js';
import './HandTrackingCam.css';

const HAND_CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [5, 9], [9, 10], [10, 11], [11, 12],
  [9, 13], [13, 14], [14, 15], [15, 16],
  [13, 17], [17, 18], [18, 19], [19, 20],
  [0, 17],
];

export default function HandTrackingCam() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const animIdRef = useRef(null);
  const lastConfettiRef = useRef(0);

  // Smoothing & persistence to eliminate flickering
  const targetHandsRef = useRef([]);
  const smoothedHandsRef = useRef([]);
  const missingFramesRef = useRef(0);

  const [cameraActive, setCameraActive] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [displayCount, setDisplayCount] = useState(0);
  const [displayGesture, setDisplayGesture] = useState(null);

  const stopCamera = useCallback(() => {
    if (animIdRef.current) {
      cancelAnimationFrame(animIdRef.current);
      animIdRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    targetHandsRef.current = [];
    smoothedHandsRef.current = [];
    setCameraActive(false);
    setDisplayCount(0);
    setDisplayGesture(null);
  }, []);

  const startLoop = useCallback(() => {
    let lastVideoTime = -1;

    const render = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      if (video && canvas && video.readyState >= 2) {
        const ctx = canvas.getContext('2d');
        const width = video.videoWidth;
        const height = video.videoHeight;

        if (canvas.width !== width || canvas.height !== height) {
          canvas.width = width;
          canvas.height = height;
        }

        // Run AI inference only when a new video frame arrives
        if (landmarkerRef.current && video.currentTime !== lastVideoTime) {
          lastVideoTime = video.currentTime;
          const startTimeMs = performance.now();
          const results = landmarkerRef.current.detectForVideo(video, startTimeMs);

          if (results && results.landmarks && results.landmarks.length > 0) {
            missingFramesRef.current = 0;
            targetHandsRef.current = results.landmarks;

            let sum = 0;
            let firstGesture = null;

            results.landmarks.forEach((hand) => {
              const info = analyzeFingers(hand);
              sum += info.count;
              if (info.gesture && !firstGesture) firstGesture = info.gesture;
            });

            setDisplayCount(sum);
            setDisplayGesture(firstGesture);

            if ((sum === 5 || sum === 10) && Date.now() - lastConfettiRef.current > 3500) {
              lastConfettiRef.current = Date.now();
              confetti({
                particleCount: 50,
                spread: 70,
                origin: { y: 0.6 },
                colors: ['#38bdf8', '#fbbf24', '#34d399', '#f472b6'],
              });
            }
          } else {
            missingFramesRef.current += 1;
            // Only clear targets if missing for 4 consecutive frames (prevents single-frame dropouts)
            if (missingFramesRef.current > 4) {
              targetHandsRef.current = [];
              setDisplayCount(0);
              setDisplayGesture(null);
            }
          }
        }

        // SMOOTHING & PERSISTENT DRAWING (Runs at 60 FPS every frame - zero flicker)
        ctx.clearRect(0, 0, width, height);

        const targets = targetHandsRef.current;
        const smoothed = smoothedHandsRef.current;

        if (targets.length > 0) {
          while (smoothed.length < targets.length) {
            smoothed.push(targets[smoothed.length].map((p) => ({ ...p })));
          }
          if (smoothed.length > targets.length) {
            smoothed.length = targets.length;
          }

          const LERP_FACTOR = 0.55;
          for (let h = 0; h < targets.length; h++) {
            const targetHand = targets[h];
            const smoothHand = smoothed[h];
            for (let p = 0; p < targetHand.length; p++) {
              smoothHand[p].x += (targetHand[p].x - smoothHand[p].x) * LERP_FACTOR;
              smoothHand[p].y += (targetHand[p].y - smoothHand[p].y) * LERP_FACTOR;
            }

            const info = analyzeFingers(smoothHand);
            drawSmoothHand(ctx, smoothHand, width, height, info.count);
          }
        } else {
          smoothedHandsRef.current = [];
        }
      }

      animIdRef.current = requestAnimationFrame(render);
    };

    render();
  }, []);

  const drawSmoothHand = (ctx, landmarks, width, height, fingerCount) => {
    ctx.save();
    // Match mirrored video feed
    ctx.scale(-1, 1);
    ctx.translate(-width, 0);

    // 1. Draw glowing bones
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = '#0284c7';
    ctx.shadowBlur = 12;

    for (const [i, j] of HAND_CONNECTIONS) {
      const p1 = landmarks[i];
      const p2 = landmarks[j];
      ctx.beginPath();
      ctx.moveTo(p1.x * width, p1.y * height);
      ctx.lineTo(p2.x * width, p2.y * height);
      ctx.stroke();
    }

    // 2. Draw glowing joints
    landmarks.forEach((pt, idx) => {
      const isTip = [4, 8, 12, 16, 20].includes(idx);
      const isWrist = idx === 0;

      ctx.beginPath();
      ctx.arc(pt.x * width, pt.y * height, isTip ? 7.5 : isWrist ? 8 : 4.5, 0, 2 * Math.PI);

      if (isTip) {
        ctx.fillStyle = '#fbbf24';
        ctx.shadowColor = '#f59e0b';
        ctx.shadowBlur = 14;
      } else if (isWrist) {
        ctx.fillStyle = '#ec4899';
        ctx.shadowColor = '#db2777';
        ctx.shadowBlur = 10;
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = '#38bdf8';
        ctx.shadowBlur = 6;
      }
      ctx.fill();
    });

    // 3. Palm badge with finger count
    const palm = landmarks[9];
    ctx.restore();
    ctx.save();

    const palmX = (1 - palm.x) * width;
    const palmY = Math.max(35, palm.y * height - 35);

    ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(palmX - 32, palmY - 20, 64, 32, 16);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 16px "Inter", "Prompt", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${fingerCount} นิ้ว`, palmX, palmY - 4);

    ctx.restore();
  };

  const startCamera = useCallback(async () => {
    setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraActive(true);
          startLoop();
        };
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Camera access error:', err);
      setErrorMsg('กรุณาอนุญาตการเข้าถึงกล้องในเบราว์เซอร์');
    }
  }, [startLoop]);

  // Initialize MediaPipe model
  useEffect(() => {
    let cancelled = false;

    async function initModel() {
      try {
        setModelLoading(true);
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
        );
        if (cancelled) return;

        const landmarker = await HandLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numHands: 2,
        });

        if (cancelled) return;
        landmarkerRef.current = landmarker;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load HandLandmarker:', err);
        if (!cancelled) {
          setErrorMsg('ไม่สามารถโหลดโมเดล AI ได้');
        }
      } finally {
        if (!cancelled) setModelLoading(false);
      }
    }

    initModel();

    return () => {
      cancelled = true;
    };
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return (
    <div className="hand-hero-root">
      {/* Title & Subtitle */}
      <div className="hand-hero-header">
        <h1 className="hand-hero-title">พลังของ Python</h1>
        <p className="hand-hero-subtitle">ตรวจจับท่าทางและจำนวนนิ้วด้วย Computer Vision</p>
      </div>

      {/* Prominent Centerpiece Camera Card */}
      <div className="hand-hero-card">
        <div className="hand-video-stage">
          <video
            ref={videoRef}
            playsInline
            muted
            className={`hand-video-feed ${cameraActive ? 'is-active' : ''}`}
          />
          <canvas ref={canvasRef} className="hand-canvas-overlay" />

          {/* Floating Glass HUD Overlay (Directly on camera - No emojis) */}
          {cameraActive && (
            <>
              <div className="hand-glass-hud animate-pop">
                <span className="hud-count-value">{displayCount}</span>
                <span className="hud-count-unit">นิ้ว</span>
                {displayGesture && (
                  <span className="hud-gesture-pill">
                    <span>{displayGesture.name}</span>
                  </span>
                )}
              </div>

              <button
                type="button"
                className="hand-btn-close-cam"
                onClick={stopCamera}
                title="ปิดกล้อง"
              >
                ปิดกล้อง
              </button>
            </>
          )}

          {/* Camera Off State (Press button to start camera) */}
          {!cameraActive && (
            <div className="hand-cam-start-box stack">
              <div className="cam-pulse-icon" aria-hidden="true">
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m22 8-6 4 6 4V8Z" />
                  <rect width="14" height="12" x="2" y="6" rx="2" ry="2" />
                </svg>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-large btn-hero-start"
                onClick={startCamera}
                disabled={modelLoading}
              >
                {modelLoading ? 'กำลังโหลดระบบ...' : 'เริ่มเปิดกล้อง'}
              </button>
              {errorMsg && <p className="hand-error-text">{errorMsg}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
